import React from 'react';
import { Container, Box, Grid, Typography, Card, CardContent } from '@mui/material';
import {
    AccessibilityNewOutlined as AccessibilityNewOutlinedIcon,
    SpeedOutlined as SpeedOutlinedIcon,
    SecurityOutlined as SecurityOutlinedIcon,
    GroupOutlined as GroupOutlinedIcon,
    PsychologyOutlined as PsychologyOutlinedIcon
} from '@mui/icons-material';

const AboutSection = () => {
    const values = [
        {
            icon: <AccessibilityNewOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: 'Dostępność',
            description: 'Find solution jest zawsze w zasięgu – bez względu na miejsce czy porę. Używasz go, gdy tylko potrzebujesz chwili na zatrzymanie i zmianę perspektywy.'
        },
        {
            icon: <SpeedOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: 'Skuteczność',
            description: 'Zamiast analizy problemów, skupiamy się na działaniu. Każda interakcja prowadzi do jasnego, gotowego do wdrożenia planu, który możesz zastosować od razu.'
        },
        {
            icon: <SecurityOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: 'Bezpieczeństwo',
            description: 'Find solution to anonimowa i spokojna przestrzeń. Nie ma tu ocen ani etykiet – możesz bezpiecznie uporządkować myśli i zobaczyć pełny obraz sytuacji.'
        },
        {
            icon: <GroupOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: 'Współtworzenie',
            description: 'Twoja opinia i sugestie mają realne znaczenie i kształtują narzędzie, które naprawdę odpowiada na Twoje potrzeby.'
        },
        {
            icon: <PsychologyOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            title: 'Etyka i Odpowiedzialność',
            description: 'Używamy technologii w sposób etyczny. Find solution ma służyć Tobie, wspierając Twoją refleksję, decyzje i codzienne, świadome działanie.'
        }
    ];

    return (
        <Box id="about-us" sx={{ py: 8, bgcolor: 'grey.50' }}>
            <Container maxWidth="lg">
                {/* Main About Content */}
                <Box sx={{ mb: 8 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: 'grey.800',
                            mb: 6
                        }}
                    >
                        O nas
                    </Typography>

                    <Typography
                        variant="h5"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: 'primary.main',
                            mb: 4
                        }}
                    >
                        Wierzymy, że masz w sobie wszystko, by ruszyć z miejsca.
                    </Typography>

                    <Box sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}>
                        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7, color: 'grey.700' }}>
                            Find solution narodził się z prostej potrzeby: znaleźć kierunek, gdy myśli krążą w kółko. Każdy zna moment, kiedy czuje, że utknął i potrzebuje jasnego planu.
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7, color: 'grey.700' }}>
                            Stworzyliśmy narzędzie, które dostarcza konkret. Find solution to Twoja droga od zagubienia do strategii — bez czekania, stresu i oceniania.
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7, color: 'grey.700' }}>
                            Po kilku minutach interakcji z aplikacją otrzymujesz Twój Pakiet Mocy – spersonalizowany plan działania, dopasowany do tego, gdzie jesteś teraz i dokąd zmierzasz. Chcemy dać Ci narzędzie by zrobić pierwszy krok w stronę spokoju i decyzji, które naprawdę mają znaczenie.
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: 'primary.main',
                                fontStyle: 'italic',
                                mt: 4
                            }}
                        >
                            Uruchom swoją zdolność zmiany. Jesteś na to gotów.
                        </Typography>
                    </Box>
                </Box>

                {/* Values Section */}
                <Box sx={{ mb: 8 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: 'grey.800',
                            mb: 6
                        }}
                    >
                        Nasze wartości
                    </Typography>

                    <Grid container spacing={2}>
                        {values.map((value, index) => (
                            <Grid item xs={12} sm={6} md={2.4} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        border: '1px solid rgba(199, 154, 95, 0.1)',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                                            {value.icon}
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: 'grey.800',
                                                textAlign: 'center',
                                                mb: 2
                                            }}
                                        >
                                            {value.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'grey.600',
                                                lineHeight: 1.6,
                                                textAlign: 'center'
                                            }}
                                        >
                                            {value.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Why We Do This Section */}
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: 3,
                    p: 6,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(199, 154, 95, 0.1)'
                }}>
                    <Typography
                        variant="h5"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: 'grey.800',
                            mb: 4
                        }}
                    >
                        Dlaczego to robimy?
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '1.1rem',
                            lineHeight: 1.7,
                            color: 'grey.700',
                            textAlign: 'center',
                            mb: 3
                        }}
                    >
                        Bo każdy z nas zna ten moment, kiedy trudno ruszyć dalej. Find solution powstał z potrzeby stworzenia przestrzeni, w której można odnaleźć kierunek – spokojnie, jasno i po swojemu.
                    </Typography>

                    <Typography
                        variant="h6"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: 'primary.main',
                            fontStyle: 'italic'
                        }}
                    >
                        Zawsze jest krok, który można wykonać. Find solution pomaga go zobaczyć.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default AboutSection;
